import { type DataFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { StatusEnum } from 'types/enums.ts'
import { MainContainer } from '~/components/layout/main-container.tsx'
import { getUserId } from '~/features/auth/auth.server.ts'
import { getCurrencyValue } from '~/features/currency/getCurrencyValue.server.ts'
import { ServiceProfile } from '~/features/home/service-profile.tsx'
import { getProfessionalReviewsSummary } from '~/features/reviews/getProfessionalReviewsSummary.server.ts'
import { prisma } from '~/utils/config/db.server.ts'
import { getDomainUrl, invariantResponse } from '~/utils/misc.tsx'

export async function loader({ params, request }: DataFunctionArgs) {
  const slug = params.rootSlug
  const professional = await prisma.user.findUnique({
    where: {
      slug: String(slug),
    },
  })

  invariantResponse(professional, 'User not found', { status: 404 })

  const serviceSlug = params.serviceSlug

  const service = await prisma.service.findFirst({
    where: {
      slug: serviceSlug,
      status: StatusEnum.ACTIVE,
      professionalId: professional.id,
    },
    include: {
      category: true,
      professional: {
        include: {
          avatarImage: { select: { id: true } },
          video: true,
        },
      },
    },
  })
  invariantResponse(service, 'Service not found', { status: 404 })

  const userId = await getUserId(request)
  const currency = await getCurrencyValue({ request, userId })

  const { stars, reviewsCount, latestReviews, reviewers } =
    await getProfessionalReviewsSummary({ professionalId: professional.id })

  const similarServices = await prisma.service.findMany({
    where: {
      id: {
        not: service.id,
      },
      status: StatusEnum.ACTIVE,
      categoryId: service.categoryId,
      professional: {
        status: StatusEnum.ACTIVE,
      },
    },
    include: {
      professional: {
        select: {
          nickname: true,
          slug: true,
          avatarImage: {
            select: { id: true },
          },
          services: {
            select: {
              serviceBooked: {
                select: {
                  review: {
                    select: {
                      stars: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    orderBy: {
      displayOrder: 'asc',
    },
    take: 4,
  })

  return json({
    service,
    currency,
    stars,
    latestReviews,
    reviewsCount,
    reviewers,
    similarServices,
    baseUrl: getDomainUrl(request),
  })
}

export default function UserProfile() {
  const data = useLoaderData<typeof loader>()
  return (
    <MainContainer>
      <ServiceProfile
        service={data.service}
        currency={data.currency}
        baseUrl={data.baseUrl}
        stars={data.stars}
        latestReviews={data.latestReviews}
        reviewsCount={data.reviewsCount}
        reviewers={data.reviewers}
        similarServices={data.similarServices}
      />
    </MainContainer>
  )
}
